import { useMemo } from "react";
import { useQueries } from "@tanstack/react-query";
import { useDataProvider } from "ra-core";

import type { CrmDataProvider } from "../providers/types";
import type { Deal } from "../types";

/**
 * Almikia Epic E7 (TASK-094), generalized to any number of stages by Epic EP
 * (TASK-107): exact open-lead count per EFFECTIVE stage
 * (`deals/stageConfig.ts#resolveEffectiveDealStages`, DATA CONTRACT EP (8):
 * the 10 system stages plus any direccion-created one), for the Dirección
 * panel's pipeline totals (`pipelineTotals.ts`'s `mergeExactStageCounts`).
 * Each stage's own `perPage: 1` query still reads a real server-side `total`,
 * exact regardless of how many leads exist, unlike the single bounded
 * `MAX_OPEN_LEADS` read `DireccionPanel.tsx` still uses for the MXN totals
 * (see this file's ADR).
 *
 * TASK-094 unrolled this into 10 explicit `useGetList` calls, one per fixed
 * Almikia stage, because the Rules of Hooks require a syntactically static
 * (non-loop) call sequence -- that only works for a FIXED-length list.
 * `stageValues` is now a variable-length effective stage list, so this uses
 * `useQueries` (`@tanstack/react-query`) instead: it is the react-query
 * primitive built for exactly this "N parallel queries, N variable at
 * render time" shape, and stays hook-rule-safe because react-query itself
 * (not React) tracks each query's identity by its `queryKey`.
 */
export interface StageOpenLeadCounts {
  /** `stage value -> exact open-lead count`, one entry per requested stage. */
  counts: Record<string, number>;
  isPending: boolean;
  error: unknown;
  refetch: () => void;
}

const PER_STAGE_PAGINATION = { page: 1, perPage: 1 } as const;

/**
 * Open leads only (DOMAIN RULE: pipeline totals never include archived
 * leads), and never test (DEMO) deals: direccion can read them under RLS but
 * they must not move a figure.
 */
export function stageCountFilter(stage: string) {
  return { stage, "archived_at@is": null, "is_test_data@neq": true };
}

export function useStageOpenLeadCounts(
  stageValues: readonly string[],
): StageOpenLeadCounts {
  const dataProvider = useDataProvider<CrmDataProvider>();

  const results = useQueries({
    queries: stageValues.map((stage) => ({
      queryKey: ["dashboard", "stageOpenLeadCount", stage],
      queryFn: () =>
        dataProvider.getList<Deal>("deals", {
          pagination: PER_STAGE_PAGINATION,
          sort: { field: "id", order: "ASC" },
          filter: stageCountFilter(stage),
        }),
      retry: false,
    })),
  });

  return useMemo(() => {
    const counts: Record<string, number> = {};
    results.forEach((result, index) => {
      if (result.data?.total != null) {
        counts[stageValues[index]] = result.data.total;
      }
    });
    return {
      counts,
      isPending: results.some((result) => result.isPending),
      error: results.find((result) => result.error)?.error,
      refetch: () => results.forEach((result) => result.refetch()),
    };
  }, [results, stageValues]);
}
